import React from "react";
import { Draggable } from "@hello-pangea/dnd";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, ExternalLink, Mail, GraduationCap, Check, Eye, MousePointerClick, RefreshCw, User as UserIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SystemUser, SystemNotification, Lead as LeadEntity } from "@/api/entities";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/lib/AuthContext";
import { shouldShowPendienteEmail, MOTIVOS_CIERRE } from "@/lib/estadosLeads";

export default function LeadCard({ lead, index, presupuesto, emailStatus, columnLeadIds, columnEstado, cleanMode }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: users = [] } = useQuery({
    queryKey: ['system_users'],
    queryFn: () => SystemUser.list()
  });

  const updateOwnerMutation = useMutation({
    mutationFn: ({ id, ...data }) => LeadEntity.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({ title: "Propietario actualizado", description: "El lead ha sido reasignado correctamente." });
    }
  });

  const { user } = useAuth();
  
  const handleOwnerChange = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const owner_id = e.target.value;
    
    // Si no ha cambiado nada, evitamos llamadas inútiles
    if (owner_id === (lead.owner_id || "unassigned")) return;

    const selectedUser = users.find(u => u.id === owner_id) || {};
    const previousOwnerEmail = lead.owner_email || "Sin asignar";
    const newOwnerEmail = selectedUser.email || "Sin asignar";

    const historialActividad = lead.historial_actividad || [];
    historialActividad.push({
      tipo: "asignacion",
      de: previousOwnerEmail,
      a: newOwnerEmail,
      usuario: user?.email || "Sistema",
      fecha: new Date().toISOString()
    });
    
    updateOwnerMutation.mutate({ 
      id: lead.id, 
      owner_id: owner_id === "unassigned" ? null : owner_id, 
      owner_email: owner_id === "unassigned" ? null : selectedUser.email,
      historial_actividad: historialActividad
    });

    // Crear notificación si se le asigna a alguién (incluido uno mismo para testing)
    if (owner_id !== "unassigned") {
      try {
        await SystemNotification.create({
          user_id: owner_id,
          message: `Se te ha asignado el lead de ${lead.nombre || 'un cliente'}`,
          lead_id: lead.id,
          read: false
        });
      } catch (error) {
        console.error("Error creando notificación:", error);
      }
    }
  };

  const getIniciales = (email) => {
    if (!email) return null;
    const nombre = email.split('@')[0];
    return nombre.charAt(0).toUpperCase();
  };

  const ultimoPresupuesto = presupuesto;
  const fechaCreacion = new Date(lead.created_date).toLocaleDateString('es-ES', { 
    day: '2-digit', 
    month: '2-digit',
    year: '2-digit'
  });

  const emailUsuario = lead.ultimo_usuario_edicion || lead.created_by;

  // #5/#13 Persistir contexto de columna para navegación "siguiente lead" + auto-avance
  const handleActivate = () => {
    if (columnLeadIds && columnLeadIds.length > 0 && columnEstado) {
      try {
        sessionStorage.setItem('kanban_focus', JSON.stringify({
          columnLeadIds,
          columnEstado,
          ts: Date.now(),
        }));
      } catch (e) { /* sessionStorage puede fallar en private mode */ }
    }
  };

  return (
    <Draggable draggableId={lead.id} index={index}>
      {(provided, snapshot) => (
        <Link to={createPageUrl(`Lead?id=${lead.id}`)} onClick={handleActivate}>
          <Card
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`cursor-move hover:shadow-lg transition-all duration-200 ${
              snapshot.isDragging 
                ? 'shadow-2xl rotate-2 scale-105' 
                : ''
            } bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 ${lead.recuperado ? 'border-l-4 border-l-orange-500' : ''}`}
          >
            <CardContent className="p-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {!cleanMode && lead.priority && (
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        lead.priority === 'alta' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]' :
                        lead.priority === 'media' ? 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.5)]' :
                        'bg-gray-400'
                      }`} title={`Prioridad: ${lead.priority}`} />
                    )}
                    <h4 className="font-semibold text-gray-900 dark:text-white line-clamp-1 text-sm flex items-center gap-2">
                      {lead.nombre}
                      {!cleanMode && lead.estimated_value > 0 && (
                        <span className="text-[10px] font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 rounded border border-green-100 dark:border-green-800" title="Valor Estimado">
                          💰 {lead.estimated_value}€
                        </span>
                      )}
                      {!cleanMode && lead.close_probability > 0 && (
                        <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded border border-indigo-100 dark:border-indigo-800" title="Probabilidad de Cierre">
                          🎯 {lead.close_probability}%
                        </span>
                      )}
                      {!cleanMode && lead.expected_value > 0 && (
                        <span className="text-[10px] font-bold text-fuchsia-600 dark:text-fuchsia-400 bg-fuchsia-50 dark:bg-fuchsia-900/30 px-1.5 py-0.5 rounded border border-fuchsia-100 dark:border-fuchsia-800" title="Valor Esperado (Impacto de Negocio)">
                          💎 {lead.expected_value}€
                        </span>
                      )}
                      {!cleanMode && lead.at_risk && (
                        <span className="text-[10px] font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 rounded border border-red-100 dark:border-red-800 animate-pulse" title="Lead en riesgo: Importante y sin actividad reciente">
                          ⚠️ En riesgo
                        </span>
                      )}
                    </h4>

                  {!cleanMode && lead.recuperado && (
                    <div className="flex-shrink-0 text-orange-500" title="Lead recuperado">
                      <RefreshCw className="w-3.5 h-3.5 animate-spin-slow" />
                    </div>
                  )}
                  {!cleanMode && lead.tipo === 'tesis' && (
                    <div className="inline-flex items-center gap-0.5 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 text-[9px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0">
                      <GraduationCap className="w-2.5 h-2.5" />
                      Tesis
                    </div>
                  )}
                  {/* Motivo de cierre: solo en columna Cerrado, con color según motivo */}
                  {lead.estado === 'cerrado' && lead.cerrado_motivo && MOTIVOS_CIERRE[lead.cerrado_motivo] && (
                    <span
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 ${MOTIVOS_CIERRE[lead.cerrado_motivo].color} ${MOTIVOS_CIERRE[lead.cerrado_motivo].text}`}
                      title={`Cerrado: ${MOTIVOS_CIERRE[lead.cerrado_motivo].label}`}
                    >
                      {MOTIVOS_CIERRE[lead.cerrado_motivo].label}
                    </span>
                  )}
                  {/* Estado del email: pendiente respuesta del cliente > tracking del último email enviado */}
                  {shouldShowPendienteEmail(lead) ? (
                    <div className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center flex-shrink-0 animate-pulse" title="Pendiente de respuesta">
                      <Mail className="w-3 h-3" />
                    </div>
                  ) : (!cleanMode && emailStatus) ? (
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {emailStatus.clicked ? (
                        <div title="Clic en enlace"><MousePointerClick className="w-4 h-4 text-purple-500" /></div>
                      ) : emailStatus.opened ? (
                        <div title="Email abierto"><Eye className="w-4 h-4 text-blue-500" /></div>
                      ) : (
                        <div title="Email enviado"><Check className="w-4 h-4 text-gray-400" /></div>
                      )}
                    </div>
                  ) : null}
                  {emailUsuario && (
                    <div className="w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                      {getIniciales(emailUsuario)}
                    </div>
                  )}
                </div>
                <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0" />
              </div>
              
              {lead.empresa && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 mb-2">
                  <Building2 className="w-3 h-3 flex-shrink-0" />
                  <span className="line-clamp-1">{lead.empresa}</span>
                </div>
              )}

              {lead.notas_internas && (
                <div className="text-xs text-gray-500 dark:text-gray-400 italic mb-2 line-clamp-2">
                  {lead.notas_internas}
                </div>
              )}

              {!cleanMode && lead.next_action && (
                <div className="flex flex-col gap-1 mb-2">
                  <div
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded border border-blue-100 dark:border-blue-800 w-fit cursor-help"
                    title={lead.ai_reason}
                  >
                    👉 {lead.next_action}
                  </div>
                  {lead.ai_reason && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 italic leading-tight">
                      {lead.ai_reason}
                    </p>
                  )}
                </div>
              )}

              {ultimoPresupuesto && ultimoPresupuesto.lineas && ultimoPresupuesto.lineas.length > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  <span className="font-medium">{ultimoPresupuesto.lineas.length} producto{ultimoPresupuesto.lineas.length !== 1 ? 's' : ''}</span>
                  {ultimoPresupuesto.lineas[0].producto_nombre && (
                    <span className="ml-1">· {ultimoPresupuesto.lineas[0].producto_nombre}</span>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                <div className="text-[10px] text-gray-400 dark:text-gray-500">
                  {fechaCreacion}
                </div>
                
                <div 
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} 
                  className="flex items-center ml-auto mr-2"
                >
                  <UserIcon className="w-3 h-3 text-gray-400 mr-1 hidden sm:block" />
                  <select 
                    value={lead.owner_id || "unassigned"} 
                    onChange={handleOwnerChange}
                    className="text-[10px] bg-gray-50 dark:bg-gray-800 border-none rounded py-0.5 px-1 cursor-pointer hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 w-[95px] text-gray-600 dark:text-gray-300 truncate"
                    disabled={updateOwnerMutation.isPending}
                    title={lead.owner_email || "Sin asignar"}
                  >
                    <option value="unassigned">Sin asignar</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.displayName || u.email}</option>
                    ))}
                  </select>
                </div>

                {(lead.importe > 0) && (
                  <div className="text-xs font-semibold text-green-600 dark:text-green-400 ml-1">
                    {lead.importe.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </Link>
      )}
    </Draggable>
  );
}